
import { createScaffolderFieldExtension, FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import type { FieldValidation } from '@rjsf/utils';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api'; // ¡Importaciones clave!
import { catalogApiRef } from '@backstage/plugin-catalog-react';


interface Template {
  name: string;
  description: string;
  type: string;
  versions: string[];
  current_version: string;
}

interface TemplateSelectorValue {
  template: string;
  version: string;
  branch: string;
  infraComponent?: string;
  artifactoryOutputs?: any;
  deployEnvironment?: string;
  skipInfraDropdown?: boolean;
}

interface TemplateSelectorFieldProps extends FieldExtensionComponentProps<TemplateSelectorValue> {
  template_type?: string;
  entity?: any;
  environment?: string;
}

export const TemplateSelectorFieldCD = (props: TemplateSelectorFieldProps) => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  // Entity resolution:
  //   - Editor config: passed directly as props.entity
  //   - Golden path (formContext.formData.entity): direct object
  //   - Golden path (formContext.formData.generalInfo.component): entity ref  fetch from catalog
  const entityFromProp = props.entity;
  const entityFromContext = (props as any).formContext?.formData?.entity;
  const entityRefFromContext = (props as any).formContext?.formData?.generalInfo?.component;
  const [fetchedEntity, setFetchedEntity] = useState<any>(null);

  useEffect(() => {
    if (entityFromProp || entityFromContext) return; // already resolved
    if (!entityRefFromContext) return;
    catalogApi.getEntityByRef(entityRefFromContext)
      .then(e => {
        setFetchedEntity(e ?? null);
      })
      .catch(err => console.error('Error fetching entity by ref:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityRefFromContext]);

  const entity = entityFromProp ?? entityFromContext ?? fetchedEntity;
  const SYS_INFO_APP_URL = 'app.baseUrl';
  const SYS_INFO_BACKEND_URL = 'backend.baseUrl';
  const appUrl = config.getString(SYS_INFO_APP_URL);
  const backendUrl = config.getString(SYS_INFO_BACKEND_URL);

  const [environment, setEnvironment] = useState('');
  const [branch, setBranch] = useState('');
  const [branches, setBranches] = useState<string[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infraComponents, setInfraComponents] = useState<any[]>([]);
  const [loadingInfra, setLoadingInfra] = useState(false);
  const [artifactoryOutputs, setArtifactoryOutputs] = useState<any>(null);
  const [loadingOutputs, setLoadingOutputs] = useState(false);
  // Whether to show the infra dropdown (only for entities belonging to PRJ0035937)
  const [showInfraDropdown, setShowInfraDropdown] = useState(false);
  // Error message when Artifactory outputs call fails (e.g. terraform apply not run)
  const [outputsError, setOutputsError] = useState<string | null>(null);

  const currentValue = props.formData || { template: '', version: '', branch: '' };
  const selectedTemplate = templates.find(t => t.name === currentValue.template);
  const hasIacTag = entity?.metadata.tags?.some((tag: string) => tag.toLowerCase() === 'iac') ?? false;
  // Resolve environment: config editor passes props.environment; golden path passes it via formContext
  const formContextEnv = (props as any).formContext?.formData?.generalInfo?.environment;
  const effectiveEnvironment = props.environment || formContextEnv;


  // Fetch templates when template_type or branch changes
  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.template_type, branch, hasIacTag]);

  // Check if this entity belongs to PRJ0035937 (the project that uses GKE infra dropdown + Artifactory flow)
  // Logic: the entity's spec.system must appear in the project's spec.applications list
  const GKE_INFRA_PROJECT_REF = 'project:default/prj0035937';
  useEffect(() => {

    if (currentValue.template !== 'helm-deploy-gke') {
      setShowInfraDropdown(false);
      return;
    }
    const entitySystem = entity?.spec?.system as string | undefined;
    if (!entitySystem) {
      setShowInfraDropdown(false);
      return;
    }
    catalogApi.getEntityByRef(GKE_INFRA_PROJECT_REF)
      .then(prjEntity => {
        const apps: string[] = (prjEntity?.spec as any)?.applications ?? [];
        const isIncluded = apps.includes(entitySystem);
        setShowInfraDropdown(isIncluded);
        if (!isIncluded) {
          // Entity does not belong to PRJ: skip infra dropdown and let editor_schema render directly
          props.onChange({ ...currentValue, skipInfraDropdown: true });
        }
      })
      .catch(_ => {
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue.template, entity?.spec?.system]);

  // Fetch IaC components when helm-deploy-gke is selected
  useEffect(() => {
    if (currentValue.template !== 'helm-deploy-gke') {
      setInfraComponents([]);
      return;
    }
    const entitySystem = entity?.spec?.system;
    if (!entitySystem) {
      console.warn('entitySystem is undefined — showing ALL IaC components (no system filter)');
    }

    setLoadingInfra(true);
    catalogApi
      .getEntities({ filter: { kind: 'Component' } })
      .then(response => {
        const withIac = response.items.filter((c: any) => c.metadata?.tags?.includes('iac'));
        // Filter by system only if entity+system are available; otherwise show all IaC components
        const filtered = entitySystem
          ? withIac.filter((c: any) => c.spec?.system === entitySystem)
          : withIac;
        setInfraComponents(filtered);
      })
      .catch(err => console.error('Error fetching IaC components:', err))
      .finally(() => setLoadingInfra(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue.template, entity?.spec?.system]);

  // Fetch Artifactory outputs when an infra component + deploy environment are selected
  // Component name format: '{division}-apm{appNumber}-{repoName}' e.g. 'ats-apm0003580-fw4-digital-hub'
  useEffect(() => {
    if (!currentValue.infraComponent || !effectiveEnvironment) {
      setArtifactoryOutputs(null);
      return;
    }
    const match = currentValue.infraComponent.match(/^(.+)-apm(\d+)-(.+)$/);
    if (!match) {
      return;
    }
    const [, division, appNumber, repoName] = match;
    const appId = `APM${appNumber}`;
    const environmentUpper = effectiveEnvironment.toUpperCase();
    setOutputsError(null);
    setLoadingOutputs(true);
    identityApi.getCredentials().then(({ token }) => {
      const url = `${backendUrl}/api/devops-api-proxy/artifactory/outputs/${division}/${appId}/${repoName}/${environmentUpper}`;
      return fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
    })
    .then(res => {
      if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
      return res.json();
    })
    .then(data => {
      setArtifactoryOutputs(data);
      props.onChange({ ...currentValue, artifactoryOutputs: data, deployEnvironment: effectiveEnvironment });
    })
    .catch(err => {
      console.error('Error fetching Artifactory outputs:', err);
      setArtifactoryOutputs(null);
      setOutputsError('No se ha encontrado una ejecución válida para el entorno seleccionado. Asegúrate de haber ejecutado en la infraestructura seleccionada.');
    })
    .finally(() => setLoadingOutputs(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue.infraComponent, effectiveEnvironment]);

  // Cuando cambia la branch, recargar templates de esa branch
  useEffect(() => {
    if (!branch) return;

    // Resetear selección actual del formulario
    props.onChange({ template: '', version: '', branch: branch });

    // Volver a cargar templates
    fetchTemplates();
  }, [branch]);


  const fetchBranches = async () => {
    try {
      const token = (await identityApi.getCredentials()).token;

      const response = await fetch(`${backendUrl}/api/devops-api-proxy/branches`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const branchesData = await response.json();
      setBranches(branchesData);

    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = (await identityApi.getCredentials()).token;

      // Determine type filter
      const typeFilter = hasIacTag ? 'iacd' : 'cd';

      const url = `${backendUrl}/api/devops-api-proxy/templates?templateBranch=${encodeURIComponent(branch)}`;

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const templatesData = await response.json();
      // Filter by type
      const filteredTemplates = (templatesData.apiPayload || []).filter((tpl: any) => tpl.type === typeFilter);
      console.log('typeFilter:', typeFilter, '| hasIacTag:', hasIacTag, '| entity:', entity, '| filteredTemplates:', filteredTemplates.length, filteredTemplates.map((t: any) => t.name));
      setTemplates(filteredTemplates);
      if (!branch || branch === '') {
        // If branch is empty, set it and re-fetch templates for the new branch
        setBranch(templatesData.defaultBranch);
        return; // Exit to avoid using old branch value
      }
      setEnvironment(templatesData.environment);

      if (templatesData.environment === 'preproduction') {
        await fetchBranches();
      }

    } catch (error) {
      console.error('Error fetching templates:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };


  const handleTemplateChange = (templateName: string) => {
    const template = templates.find(t => t.name === templateName);
    const newValue = {
      branch: branch,
      template: templateName,
      version: template?.current_version || '' // Always use current version
    };
    props.onChange(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gridGap={2} p={2}>
        <CircularProgress size={20} />
        <Typography>Loading templates...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error" style={{ marginBottom: 16 }}>
          Error loading templates: {error}
        </Alert>
        <button
          onClick={fetchTemplates}
          style={{
            padding: '8px 16px',
            // backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
           Retry
        </button>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Typography variant="h6" gutterBottom>
         Template Configuration
      </Typography>

      <Box display="flex" gridGap={2} flexDirection="column">
        {/* Template Selector */}
        {environment === 'preproduction' && (
          <FormControl fullWidth variant="standard">
            <InputLabel>Branch</InputLabel>
            <Select
              value={currentValue.branch}
              onChange={(e) => setBranch(e.target.value as string)}
              label="Branch"
            >
              {branches.map(b => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl fullWidth variant="standard">
          <InputLabel>Template</InputLabel>
          <Select
            value={currentValue.template}
            onChange={(e) => handleTemplateChange(e.target.value as string)}
            label="Template"
          >
            <MenuItem value="">
              <em>Select a template</em>
            </MenuItem>
            {/* Infra-driven flow: the preselected template may be hidden from
                this list by type; keep a hidden item so the Select value is
                valid without offering it for manual selection. */}
            {currentValue.template && !templates.some(t => t.name === currentValue.template) && (
              <MenuItem value={currentValue.template} style={{ display: 'none' }}>
                {currentValue.template}
              </MenuItem>
            )}
            {templates.map((template) => (
              <MenuItem key={template.name} value={template.name}>
                <Box>
                  <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                    {template.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {template.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Version Selector removed: always use current version */}

        {/* Selected Template Info */}
        {selectedTemplate && (
          <Box
            mt={2}
            p={2}
            style={{
              // backgroundColor: '#343639ff',
              borderRadius: '4px',
              border: '1px solid #3e404bff'
            }}
          >
            <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
               Selected Template Details
            </Typography>
            <Typography variant="body2">
              <strong>Name:</strong> {selectedTemplate.name}
            </Typography>
            <Typography variant="body2">
              <strong>Description:</strong> {selectedTemplate.description}
            </Typography>
            <Typography variant="body2">
              <strong>Type:</strong> {selectedTemplate.type}
            </Typography>
            {/* <Typography variant="body2">
              <strong>Available Versions:</strong> {selectedTemplate.versions.join(', ')}
            </Typography> */}
            <Typography variant="body2">
              <strong>Current Version:</strong> {selectedTemplate.current_version}
            </Typography>
          </Box>
        )}
        {['helm-deploy-eks', 'helm-deploy-gke'].includes(selectedTemplate?.name || '') && (
          <Box mt={2}>
            <Alert severity="warning">
              <strong>Prerequisite required:</strong> This deployment template depends on a previously created CI pipeline.
              <br /><br />
              Before using <code>{selectedTemplate?.name}</code>, your team must have created a project using the <strong>helm-build</strong> CI template.
              <br /><br />
               
              <a
                href={`${appUrl}/docs/default/component/backstage-docs/repo-templates/CD/${selectedTemplate?.name === 'helm-deploy-eks' ? 'AWS-Helm-EKS' : 'Google-Helm-GKE'}/#prerequisito-importante`}
                target="_blank"
                rel="noopener noreferrer"
              >
                documentation
              </a>
            </Alert>
          </Box>
        )}
        {showInfraDropdown && loadingOutputs && (
          <Box display="flex" alignItems="center" gridGap={8} mt={1}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="textSecondary">Cargando valores de infra...</Typography>
          </Box>
        )}
        {showInfraDropdown && artifactoryOutputs && !loadingOutputs && (
          <Alert severity="success" style={{ marginTop: 8 }}>
            Valores cargados.
          </Alert>
        )}
        {showInfraDropdown && outputsError && !loadingOutputs && (
          <Alert severity="error" style={{ marginTop: 8 }}>
            {outputsError}
          </Alert>
        )}
        {showInfraDropdown && (
          <Box mt={2}>
            <FormControl fullWidth variant="standard">
              <InputLabel>Entorno Digital Hub donde se quiere desplegar</InputLabel>
              <Select
                value={currentValue.infraComponent || ''}
                onChange={(e) => {
                  props.onChange({ ...currentValue, infraComponent: e.target.value as string, artifactoryOutputs: null, deployEnvironment: undefined });
                  setArtifactoryOutputs(null);
                  setOutputsError(null);
                }}
                label="Infra donde está desplegado"
                disabled={loadingInfra || loadingOutputs}
              >
                <MenuItem value="">
                  <em>{loadingInfra ? 'Cargando...' : 'Selecciona una infra'}</em>
                </MenuItem>
                {infraComponents.map((comp: any) => (
                  <MenuItem key={comp.metadata.name} value={comp.metadata.name}>
                    {comp.metadata.title || comp.metadata.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const templateSelectorCaseValidation = (
  value: TemplateSelectorValue,
  validation: FieldValidation,
) => {
  if (!value) {
    validation.addError('This field is required.');
    return;
  }
};

// Create the extension

export const TemplateSelectorCaseFieldExtensionCD = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'TemplateSelectorFieldCD',
    component: TemplateSelectorFieldCD,
    validation: templateSelectorCaseValidation,
  }),
);

